import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useUnmount } from 'react-use';

import { KnmSubjectTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmSubjectTypeCodesEnum';
import { IImportTemplate } from 'APIPackage/ImportExportModule/interfaces/IImportTemplate';
import { useKnmTypes } from 'CorePackage/CatalogsModule/hooks/useKnmTypes';
import { useKnmSubjectTypes } from 'CorePackage/CatalogsModule/hooks/useKnmSubjectTypes';
import { IKnmSubjectTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmSubjectTypeSelectOption';
import { IKnmSupervisionTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmSupervisionTypeSelectOption';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { isFileExtensionValid } from 'CorePackage/MainModule/validators/isFileExtensionValid';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { UploadPlanIdField } from 'ImportPackage/MainModule/components/UploadPlanIdField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ISelectOption, SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { Button } from 'UIPackage/MainModule/components/Button';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { makeIsNumber } from 'CorePackage/MainModule/validators/isNumber';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { accountKnoOrganizationFunctionsGet } from 'CorePackage/AccountModule/actions/accountActions';
import {
  getAccountKnoOrganizationFunctionsGroupSelectOptions,
  getAccountKnoOrganizationsSelectOptions,
  getAccountProsecutorOrganizationsSelectOptions,
} from 'CorePackage/AccountModule/selectors/accountSelectors';
import { UploadFileTypesEnum } from 'CorePackage/MainModule/enums/UploadFileTypesEnum';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import {
  getDictionariesKnoOrganizationsSelectOptions,
  getFilteredFederalLawsSelectOptions,
  getPlanTypeWithFederalLawSelectOptions,
  makeGetDictionariesSupervisionTypesExtendedSelectOptions,
  makeGetDictionariesTargetEntitySelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  dictionariesFilteredFederalLawsGet,
  dictionariesKnmSubjectTypesGet,
  dictionariesKnoOrganizationsItemGet,
  dictionariesTargetEntityItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { makeGetCatalogKnmSupervisionTypesExtendedSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { ISupervisionType } from 'APIPackage/DictionariesModule/interfaces/ISupervisionType';
import { IKnmSubjectTypesERVK } from 'APIPackage/CatalogsModule/interfaces/IKnmSubjectTypesERVK';
import { IFederalLawErvk } from 'APIPackage/CatalogsModule/interfaces/IFederalLawErvk';

import styles from './UploadModal.module.scss';
import {
  getImportPlanTypeFederalLawErvkId,
  getImportKnmTypeId,
  getImportKnoOrganizationErvkId,
  getImportKnoOrganizationId,
  getImportSubjectTypeId,
  getImportSubjectTypeErvkId,
  getImportSupervisionTypeErvkId,
  getImportFederalLawErvkId,
  getImportTemplate,
} from '../selectors/importSelectors';
import {
  importResetUploadModalStore,
  importSetFederalLaw,
  importSetKnmTypeId,
  importSetKnoOrganizationErvkId,
  importSetKnoOrganizationId,
  importSetPlanTypeWithFederalLaw,
  importSetSubjectTypeErvkId,
  importSetSubjectTypeId,
  importSetSupervisionTypeErvkId,
} from '../actions/importActions';
import modalStyles from '../../../KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal.module.scss';
import { IPlanTypeWithFederalLaw } from '../interfaces/IPlanTypeWithFederalLaw';

const validExtensions = ['ods', 'xls', 'xlsx', 'xml'];
const validExtensionsString = `.${validExtensions.join(',.')}`;

const isErvkSubjectTypeFileType = (fileTypeId?: keyof typeof UploadFileTypesEnum) =>
  !!fileTypeId &&
  (UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN ||
    UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN_CORRECTION);

interface IFileTypeSelectOption {
  label: ISelectOption['label'];
  value: keyof typeof UploadFileTypesEnum;
  id: keyof typeof UploadFileTypesEnum;
}

interface IProsecutorOrganizationSelectOption extends Omit<ISelectOption, 'id'> {
  id: string;
}

interface IKnoOrganizationSelectOption extends Omit<ISelectOption, 'id'> {
  id: string;
  idStr?: string;
}

interface IKnoOrganizationFunctionSelectOption extends Omit<ISelectOption, 'id'> {
  id: number;
}

type IErvkSupervisionTypeOption = IDictionarySelectOption<ISupervisionType> & { source: 'ervk' };

type IMergedSupervisionTypeOption = IErvkSupervisionTypeOption | (IKnmSupervisionTypeSelectOption & { source: 'frgu' });

export interface IUploadModalForm {
  prosecutorOrganization: IProsecutorOrganizationSelectOption;
  knoOrganization: IKnoOrganizationSelectOption;
  knoOrganizationFunction?: IKnoOrganizationFunctionSelectOption;
  subjectType?: IKnmSubjectTypeSelectOption;
  subjectTypeErvk?: IDictionarySelectOption<IKnmSubjectTypesERVK>;
  supervisionType: IKnmSupervisionTypeSelectOption;
  supervisionTypeErvk?: IDictionarySelectOption<ISupervisionType>;
  planTypeWithFederalLaw?: IDictionarySelectOption<IPlanTypeWithFederalLaw>;
  federalLaw?: IDictionarySelectOption<IFederalLawErvk>;
  fileType: IFileTypeSelectOption;
  file: File;
  approval: string;
  name: string;
  planId: number | null;
  knmType?: IKnmTypeSelectOption;
  erpId?: string;
}

type IUploadModalFormRenderProps = FormRenderProps<IUploadModalForm>;

interface IUploadModalProps {
  visible?: boolean;
  fileTypes: (keyof typeof UploadFileTypesEnum)[];
  isAdjustment?: boolean;
  onApply: (values: IUploadModalForm) => void;
  onClose: () => void;
}

interface IUploadModalStoreProps {
  isErknm: boolean;
  plan?: IKnmPlan;
  planImportTemplate: IImportTemplate | undefined;
  subjectTypeErvkId: string | undefined;
  userKnoOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  knoOrganisationFunctionsOptions: IKnoOrganizationFunctionSelectOption[] | undefined;
  knoOrganizationId: string | undefined;
  knoOrganizationErvkId: string | undefined;
  supervisionTypeErvkId: string | undefined;
  erknmKnoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  isSupervisor: boolean | undefined;
  knmTypeId: number | undefined;
  subjectTypeId: number | undefined;
  erknmSupervisionTypesSelectOptions: IDictionarySelectOption<ISupervisionType>[] | undefined;
  erknmSupervisionTypesExcluding248SelectOptions: IDictionarySelectOption<ISupervisionType>[] | undefined;
  erpSupervisionTypesSelectOptions: IDictionarySelectOption<ISupervisionType>[] | undefined;
  knmSupervisionTypesSelectOptions: IKnmSupervisionTypeSelectOption[] | undefined;
  knmSubjectTypesErvkSelectOptions: IDictionarySelectOption<IKnmSubjectTypesERVK>[] | undefined;
  federalLawsWithPlanTypeSelectOptions: IDictionarySelectOption<IPlanTypeWithFederalLaw>[] | undefined;
  federalLawsSelectOptions: IDictionarySelectOption<IFederalLawErvk>[] | undefined;
}

const UploadModalWithoutStore = ({
  visible,
  fileTypes,
  isErknm,
  isAdjustment,
  plan,
  planImportTemplate,
  subjectTypeErvkId,
  knoOrganizationId,
  knoOrganizationErvkId,
  supervisionTypeErvkId,
  knoOrganisationFunctionsOptions,
  userKnoOrganisationsSelectOptions,
  userProsecutorOrganisationsSelectOptions,
  erknmKnoOrganizationsSelectOptions,
  isSupervisor,
  knmTypeId,
  subjectTypeId,
  erknmSupervisionTypesSelectOptions,
  erknmSupervisionTypesExcluding248SelectOptions,
  erpSupervisionTypesSelectOptions,
  knmSupervisionTypesSelectOptions,
  knmSubjectTypesErvkSelectOptions,
  federalLawsWithPlanTypeSelectOptions,
  federalLawsSelectOptions,
  onApply,
  onClose,
}: IUploadModalProps & IUploadModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [formInstance, setFormInstance] = useState<IUploadModalFormRenderProps['form']>();
  const getFormFileTypeId = useCallback(() => {
    return formInstance?.getFieldState('fileType')?.value?.id;
  }, [formInstance]);

  useUnmount(() => {
    dispatch(importResetUploadModalStore());
  });

  useEffect(() => {
    const options: IFileTypeSelectOption[] = fileTypes.map(fileType => ({
      label: UploadFileTypesEnum[fileType],
      name: UploadFileTypesEnum[fileType],
      value: fileType,
      id: fileType,
    }));

    setFileTypesSelectOptions(options);
  }, [fileTypes]);

  useEffect(() => {
    if (isErknm) {
      dispatch(dictionariesKnoOrganizationsItemGet());
    } else {
      dispatch(dictionariesKnmSubjectTypesGet());
    }
  }, [dispatch, isErknm]);

  const [fileTypesSelectOptions, setFileTypesSelectOptions] = useState<IFileTypeSelectOption[]>([]);
  const { knmTypesSelectOptions } = useKnmTypes(isErknm);
  const { knmSubjectTypesSelectOptions: subjectTypesOptionsUnsorted } = useKnmSubjectTypes();
  const [subjectTypesOptions, setSubjectTypesOptions] = useState<IKnmSubjectTypeSelectOption[]>([]);

  const knmTypesSelectOptionsByFiletype = useCallback(
    (fileTypeId: keyof typeof UploadFileTypesEnum) => {
      if (UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN) {
        return knmTypesSelectOptions.filter(item => item.value === 'PP');
      }
      if (
        (UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.KNM && subjectTypeId !== 0) ||
        (fileTypeId === undefined && subjectTypeId === 1 && isSupervisor)
      ) {
        return knmTypesSelectOptions.filter(option => option.id !== 2);
      }

      return knmTypesSelectOptions;
    },
    [knmTypesSelectOptions, subjectTypeId, isSupervisor],
  );

  const knmSubjectTypesSelectOptionsByFiletype = useCallback(
    (fileTypeId: keyof typeof UploadFileTypesEnum) => {
      if (isErvkSubjectTypeFileType(fileTypeId)) {
        return knmSubjectTypesErvkSelectOptions;
      }
      // (knmTypeId === 2) - проверка на тип "Контрольная закупка"
      if (UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.KNM && knmTypeId === 2) {
        return [subjectTypesOptions[2]];
      }

      return subjectTypesOptions;
    },
    [knmSubjectTypesErvkSelectOptions, subjectTypesOptions, knmTypeId],
  );

  useEffect(() => {
    const supervisionTypeFormValue: IKnmSupervisionTypeSelectOption | undefined = formInstance?.getFieldState(
      'supervisionType',
    )?.value;

    if (supervisionTypeFormValue) {
      const newSupervisionTypeFormValue = knmSupervisionTypesSelectOptions?.find(
        supervisionTypeOption => supervisionTypeOption.digitCode === supervisionTypeFormValue.digitCode,
      );

      /* безусловный change затирает значение в окне между сменой типа лица и приходом данных
       * добавлена защита, не трогать поле, если совпадение не найдено */
      if (newSupervisionTypeFormValue) {
        formInstance?.change('supervisionType', newSupervisionTypeFormValue);
      }
    }
  }, [knmSupervisionTypesSelectOptions, formInstance]);

  useEffect(() => {
    const options: IKnmSubjectTypeSelectOption[] = subjectTypesOptionsUnsorted.sort((a, b) => {
      if (a.code === KnmSubjectTypeCodesEnum.UL_IP) {
        return 1;
      }
      if (b.code === KnmSubjectTypeCodesEnum.UL_IP) {
        return -1;
      }

      return 0;
    });

    setSubjectTypesOptions(options);
  }, [subjectTypesOptionsUnsorted]);

  useEffect(() => {
    if (!isErknm && knoOrganizationId) {
      dispatch(accountKnoOrganizationFunctionsGet({ knoOrganizationId, isCaching: true }));
    }
  }, [dispatch, isErknm, knoOrganizationId]);

  useEffect(() => {
    if (!isErknm && knoOrganizationErvkId && supervisionTypeErvkId) {
      dispatch(
        dictionariesTargetEntityItemGet({
          knoVersionId: knoOrganizationErvkId,
          supervisionVersionId: supervisionTypeErvkId,
        }),
      );
    }
  }, [dispatch, isErknm, knoOrganizationErvkId, supervisionTypeErvkId]);

  const handleFormSubmit = useCallback(
    (values: IUploadModalForm) => {
      if (Object.keys(values).length) {
        onApply(values);
        onClose();
      }
    },
    [onApply, onClose],
  );

  const handleAfterClose = useCallback(() => {
    if (formInstance) {
      formInstance.reset();
    }
  }, [formInstance]);

  const resetSubjectTypeErvk = useCallback(
    (form: IUploadModalFormRenderProps['form']) => {
      form.change('subjectTypeErvk', undefined);
      dispatch(importSetSubjectTypeErvkId(undefined));
    },
    [dispatch],
  );

  const prevErvkFilterRef = useRef({ knoOrganizationErvkId, supervisionTypeErvkId });

  useEffect(() => {
    const prev = prevErvkFilterRef.current;

    if (prev.knoOrganizationErvkId === knoOrganizationErvkId && prev.supervisionTypeErvkId === supervisionTypeErvkId) {
      return;
    }

    prevErvkFilterRef.current = { knoOrganizationErvkId, supervisionTypeErvkId };

    if (formInstance?.getFieldState('subjectTypeErvk')?.value) {
      resetSubjectTypeErvk(formInstance);
    }
  }, [formInstance, knoOrganizationErvkId, supervisionTypeErvkId, resetSubjectTypeErvk]);

  const handleFileTypeFieldChange = (form: IUploadModalFormRenderProps['form']) => () => {
    form
      .getRegisteredFields()
      .forEach(
        (registeredField: keyof IUploadModalForm) =>
          registeredField !== 'fileType' &&
          registeredField !== 'file' &&
          registeredField !== 'prosecutorOrganization' &&
          form.change(registeredField, undefined),
      );
    form.change('supervisionType', undefined);
    form.change('supervisionTypeErvk', undefined);
    dispatch(importSetSupervisionTypeErvkId(undefined));
    resetSubjectTypeErvk(form);
  };

  const prevImportTemplateRef = useRef<IImportTemplate | undefined>(undefined);

  const handleImportByPlanChange = useCallback(
    (importTemplate: IImportTemplate | undefined) => {
      const fileTypeId = formInstance?.getFieldState('fileType')?.value?.id;

      if (!fileTypeId || UploadFileTypesEnum[fileTypeId] !== UploadFileTypesEnum.PLAN_CORRECTION || !formInstance) {
        return;
      }

      if (importTemplate) {
        formInstance.change(
          'knmType',
          knmTypesSelectOptions.find(knmTypeSelectOption => knmTypeSelectOption.id === 0),
        );
        formInstance.change(
          'knoOrganization',
          userKnoOrganisationsSelectOptions.find(
            knoOrganisationSelectOption =>
              knoOrganisationSelectOption.id === String(importTemplate.knoOrganization?.id),
          ),
        );
        formInstance.change(
          'knoOrganizationFunction',
          knoOrganisationFunctionsOptions?.find(
            knoOrganisationFunctionSelectOption =>
              knoOrganisationFunctionSelectOption?.id === importTemplate.knoOrganizationFunction?.id,
          ),
        );
        formInstance.change(
          'prosecutorOrganization',
          userProsecutorOrganisationsSelectOptions.find(
            prosecutorOrganisationSelectOption =>
              prosecutorOrganisationSelectOption.id === String(importTemplate.ervkProsecutorOrganization?.id) ||
              prosecutorOrganisationSelectOption.id ===
                String(importTemplate.ervkProsecutorOrganization?.ervkProsecutorOrganizationID),
          ),
        );
        formInstance.change('planId', importTemplate.planId);
      } else if (prevImportTemplateRef.current) {
        formInstance.change('knmType', undefined);
        formInstance.change('supervisionType', undefined);
        formInstance.change('supervisionTypeErvk', undefined);
        formInstance.change('knoOrganization', undefined);
        formInstance.change('knoOrganizationFunction', undefined);
        dispatch(importSetSupervisionTypeErvkId(undefined));
        resetSubjectTypeErvk(formInstance);
      }

      prevImportTemplateRef.current = importTemplate;
    },
    [
      dispatch,
      formInstance,
      knmTypesSelectOptions,
      userKnoOrganisationsSelectOptions,
      knoOrganisationFunctionsOptions,
      userProsecutorOrganisationsSelectOptions,
      resetSubjectTypeErvk,
    ],
  );

  const handleKnoOrganizationChange = useCallback(
    (form: IUploadModalFormRenderProps['form']) => (
      value?: IDictionarySelectOption<IKnoOrganizationDictionary> | IUserOrganisationSelectOption,
    ) => {
      form.change('planTypeWithFederalLaw', undefined);
      form.change('supervisionType', undefined);
      form.change('supervisionTypeErvk', undefined);

      dispatch(importSetPlanTypeWithFederalLaw(undefined));
      dispatch(importSetSupervisionTypeErvkId(undefined));

      dispatch(importSetKnoOrganizationId(value?.id));
      if (value && 'ervkVersionUuid' in value && value.ervkVersionUuid) {
        dispatch(dictionariesFilteredFederalLawsGet(value.ervkVersionUuid, false));
        dispatch(importSetKnoOrganizationErvkId(value.ervkVersionUuid));
      } else {
        dispatch(importSetKnoOrganizationErvkId(undefined));
      }
    },
    [dispatch],
  );

  const handleErknmKnoOrganizationChange = useCallback(
    (value?: IDictionarySelectOption<IKnoOrganizationDictionary> | IUserOrganisationSelectOption) => {
      dispatch(
        importSetKnoOrganizationErvkId(
          value && 'value' in value && typeof value.value !== 'string' ? value.value.dictVersionId : undefined,
        ),
      );
    },
    [dispatch],
  );

  const handleChangeKnmType = useCallback(
    (value?: IKnmTypeSelectOption) => {
      dispatch(importSetKnmTypeId(value?.id));
    },
    [dispatch],
  );

  const handleChangeSubjectType = useCallback(
    (value?: IKnmSubjectTypeSelectOption) => {
      dispatch(importSetSubjectTypeId(value?.id));
    },
    [dispatch],
  );

  const handleChangeSubjectTypeErvk = useCallback(
    (value?: IDictionarySelectOption<IKnmSubjectTypesERVK>) => {
      dispatch(importSetSubjectTypeErvkId(value?.value.dictVersionId));
    },
    [dispatch],
  );

  const handleChangeSupervisionType = useCallback(
    (fileTypeId: keyof typeof UploadFileTypesEnum, form: IUploadModalFormRenderProps['form']) => (
      value: IDictionarySelectOption<ISupervisionType> | undefined,
    ) => {
      if (UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN) {
        form.change('supervisionTypeErvk', value);
        dispatch(importSetSupervisionTypeErvkId(value?.value.dictVersionId));
      }
    },
    [dispatch],
  );

  const handleChangePlanType = useCallback(
    (form: IUploadModalFormRenderProps['form']) => (value?: IDictionarySelectOption<IPlanTypeWithFederalLaw>) => {
      form.change('supervisionType', undefined);
      form.change('supervisionTypeErvk', undefined);
      dispatch(importSetSupervisionTypeErvkId(undefined));
      dispatch(importSetPlanTypeWithFederalLaw(value));
    },
    [dispatch],
  );

  const handleChangeFederalLaw = useCallback(
    (value?: IDictionarySelectOption<IFederalLawErvk>) => {
      dispatch(importSetFederalLaw(value));
    },
    [dispatch],
  );

  const supervisionTypeOptions = useMemo((): IMergedSupervisionTypeOption[] => {
    if (!knoOrganizationId) {
      return [];
    }

    return [
      ...(erknmSupervisionTypesExcluding248SelectOptions ?? []).map(
        (option): IMergedSupervisionTypeOption => ({ ...option, source: 'ervk' }),
      ),
      ...(knmSupervisionTypesSelectOptions ?? []).map(
        (option): IMergedSupervisionTypeOption => ({ ...option, source: 'frgu' }),
      ),
    ];
  }, [knoOrganizationId, erknmSupervisionTypesExcluding248SelectOptions, knmSupervisionTypesSelectOptions]);

  const handleChangeSupervisionTypeMerged = useCallback(
    (fileTypeId: keyof typeof UploadFileTypesEnum, form: IUploadModalFormRenderProps['form']) => (
      value: IMergedSupervisionTypeOption | undefined,
    ) => {
      if (isErvkSubjectTypeFileType(fileTypeId)) {
        dispatch(importSetSupervisionTypeErvkId(value?.source === 'ervk' ? value.value.dictVersionId : undefined));
      }

      if (value?.source === 'ervk') {
        form.change('supervisionTypeErvk', value);
        form.change('supervisionType', undefined);

        return;
      }

      if (value?.source === 'frgu') {
        form.change('supervisionType', value);
        form.change('supervisionTypeErvk', undefined);

        return;
      }

      form.change('supervisionType', undefined);
      form.change('supervisionTypeErvk', undefined);
    },
    [dispatch],
  );

  useEffect(() => {
    if (!formInstance || !planImportTemplate) {
      return;
    }

    const { values } = formInstance.getState();
    const fileTypeId = values.fileType?.id;

    if (!fileTypeId || UploadFileTypesEnum[fileTypeId] !== UploadFileTypesEnum.PLAN_CORRECTION) {
      return;
    }

    if (!values.supervisionType && !values.supervisionTypeErvk && supervisionTypeErvkId) {
      const supervisionTypeErvkOption = supervisionTypeOptions.find(
        (option): option is IErvkSupervisionTypeOption =>
          option.source === 'ervk' && option.value.dictVersionId === supervisionTypeErvkId,
      );

      if (supervisionTypeErvkOption) {
        formInstance.change('supervisionTypeErvk', supervisionTypeErvkOption);
      }
    }

    if (!values.subjectTypeErvk && subjectTypeErvkId) {
      const subjectTypeErvkOption = knmSubjectTypesErvkSelectOptions?.find(
        option => option.value.dictVersionId === subjectTypeErvkId,
      );

      if (subjectTypeErvkOption) {
        formInstance.change('subjectTypeErvk', subjectTypeErvkOption);
      }
    }
  }, [
    formInstance,
    planImportTemplate,
    supervisionTypeErvkId,
    subjectTypeErvkId,
    supervisionTypeOptions,
    knmSubjectTypesErvkSelectOptions,
  ]);

  function renderForm({ form, handleSubmit, values }: FormRenderProps<IUploadModalForm>) {
    if (!formInstance && form) {
      setFormInstance(form);
    }
    const fileTypeId = values.fileType?.id;

    if (!values.knoOrganization?.id && values.knoOrganizationFunction) {
      form.change('knoOrganizationFunction', undefined);
    }
    const { file } = values;
    const fileName = file?.name;
    const fileSize = file?.size ?? 0;
    const fileType = file?.type ?? undefined;
    const isFileValid = file && isFileExtensionValid(fileName, validExtensions);

    const isDisabledSubmit =
      form
        .getRegisteredFields()
        .filter(item => item !== 'knoOrganizationFunction')
        .some(item => !Object.keys(values).includes(item)) ||
      (!isErknm &&
        UploadFileTypesEnum[fileTypeId] !== UploadFileTypesEnum.CHECKLIST &&
        !values.supervisionType &&
        !values.supervisionTypeErvk);

    return (
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Загрузка файла с внешнего носителя" />
        <ModalBody border="bottom">
          <div className={styles.Content}>
            {UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.ERKNM_PLAN_CORRECTION && (
              <Row>
                <div className={modalStyles.InfoBlock}>
                  <span className={modalStyles.InfoIcon}></span>
                  <span className={modalStyles.InfoText}>
                    При загрузке плана будут учитываться только столбцы &quot;Решение по включению в план &quot; и
                    &quot;Комментарии&quot;
                  </span>
                </div>
              </Row>
            )}

            <Row>
              <FieldBlock label="Тип загружаемого файла" labelFontSize="small">
                <SelectInputField
                  id="fileType"
                  name="fileType"
                  options={fileTypesSelectOptions}
                  defaultValue={
                    isAdjustment
                      ? fileTypesSelectOptions.find(
                          item => UploadFileTypesEnum[item.id] === UploadFileTypesEnum.ERKNM_PLAN_CORRECTION,
                        )
                      : undefined
                  }
                  disabled={isAdjustment}
                  onChange={handleFileTypeFieldChange(form)}
                />
              </FieldBlock>
            </Row>
            {UploadFileTypesEnum[fileTypeId] !== UploadFileTypesEnum.CHECKLIST && (
              <Row>
                <FieldBlock label="Наименование Прокуратуры" labelFontSize="small">
                  <SelectInputField
                    id="prosecutorOrganization"
                    name="prosecutorOrganization"
                    options={userProsecutorOrganisationsSelectOptions}
                    defaultValue={userProsecutorOrganisationsSelectOptions?.find(item => item.byDefault === true)}
                    disabled={true}
                  />
                </FieldBlock>
              </Row>
            )}
            {!isErknm && (
              <Row>
                <FieldBlock label="Орган контроля" labelFontSize="small">
                  <SelectInputField
                    id="knoOrganization"
                    name="knoOrganization"
                    options={userKnoOrganisationsSelectOptions}
                    isSearchable
                    onChange={handleKnoOrganizationChange(form)}
                  />
                </FieldBlock>
              </Row>
            )}
            {!isErknm && UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN && (
              <Row>
                <FieldBlock label="Тип плана КНМ" labelFontSize="small">
                  <SelectInputField
                    id="planTypeWithFederalLaw"
                    name="planTypeWithFederalLaw"
                    isClearable={false}
                    disabled={isErknm}
                    options={federalLawsWithPlanTypeSelectOptions}
                    onChange={handleChangePlanType(form)}
                  />
                </FieldBlock>
              </Row>
            )}
            {!isErknm &&
              (UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.KNM ||
                UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.KNM_CORRECTION) &&
              knoOrganizationErvkId && (
                <Row>
                  <FieldBlock
                    label="Федеральный закон, в соответствии с которым проводится проверка"
                    labelFontSize="small"
                  >
                    <SelectInputField
                      id="federalLaw"
                      name="federalLaw"
                      isClearable={false}
                      options={federalLawsSelectOptions}
                      onChange={handleChangeFederalLaw}
                    />
                  </FieldBlock>
                </Row>
              )}
            {!isErknm && UploadFileTypesEnum[fileTypeId] !== UploadFileTypesEnum.CHECKLIST && (
              <Row>
                <FieldBlock label="Вид государственного контроля (надзора)" labelFontSize="small">
                  <SelectInput
                    id="supervisionType"
                    name={
                      UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN
                        ? 'supervisionTypeErvk'
                        : 'supervisionType'
                    }
                    options={
                      UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN
                        ? erpSupervisionTypesSelectOptions
                        : supervisionTypeOptions
                    }
                    onChange={
                      UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN
                        ? handleChangeSupervisionType(fileTypeId, form)
                        : handleChangeSupervisionTypeMerged(fileTypeId, form)
                    }
                    value={
                      UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN
                        ? values.supervisionTypeErvk
                        : values.supervisionType ?? values.supervisionTypeErvk
                    }
                    noOptionsMessage="Для выбранного КНО и ФЗ по данным ЕРВК отсутствует вид контроля для плановых мероприятий"
                    isSearchable
                  />
                </FieldBlock>
              </Row>
            )}
            {UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN_CORRECTION && (
              <Row>
                <FieldBlock label="Номер плана" labelFontSize="small">
                  <UploadPlanIdField getFormFileTypeId={getFormFileTypeId} onImportChange={handleImportByPlanChange} />
                </FieldBlock>
              </Row>
            )}
            {(UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.KNM_CORRECTION ||
              UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.ERKNM_CORRECTION) && (
              <Row>
                <FieldBlock label="Учетный номер КНМ" labelFontSize="small">
                  <TextInputField
                    id="erpId"
                    name="erpId"
                    validate={composeValidators(
                      makeIsNumber('Неверный формат строки, в поле можно ввести только цифры'),
                    )}
                  />
                </FieldBlock>
              </Row>
            )}
            {UploadFileTypesEnum[fileTypeId] !== UploadFileTypesEnum.CHECKLIST && (
              <>
                {!isErknm && (
                  <>
                    <Row>
                      <FieldBlock label="Характер КНМ" labelFontSize="small" size="half">
                        <SelectInputField
                          id="knmType"
                          name="knmType"
                          options={knmTypesSelectOptionsByFiletype(fileTypeId)}
                          onChange={handleChangeKnmType}
                        />
                      </FieldBlock>
                      <FieldBlock label="Тип проверяемого лица" labelFontSize="small" size="half">
                        <SelectInputField
                          id="subjectType"
                          name={isErvkSubjectTypeFileType(fileTypeId) ? 'subjectTypeErvk' : 'subjectType'}
                          options={knmSubjectTypesSelectOptionsByFiletype(fileTypeId)}
                          onChange={
                            isErvkSubjectTypeFileType(fileTypeId)
                              ? handleChangeSubjectTypeErvk
                              : handleChangeSubjectType
                          }
                        />
                      </FieldBlock>
                    </Row>
                  </>
                )}
                {isErknm && (
                  <>
                    <Row>
                      <FieldBlock label="Орган контроля" labelFontSize="small">
                        <SelectInputField
                          id="knoOrganization"
                          name="knoOrganization"
                          options={erknmKnoOrganizationsSelectOptions}
                          isSearchable
                          defaultValue={
                            plan && isAdjustment
                              ? erknmKnoOrganizationsSelectOptions?.find(item => item.id === plan.knoId)
                              : undefined
                          }
                          disabled={
                            UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.PLAN_CORRECTION || isAdjustment
                          }
                          onChange={handleErknmKnoOrganizationChange}
                        />
                      </FieldBlock>
                    </Row>
                    {(UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.ERKNM_CORRECTION ||
                      UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.ERKNM) && (
                      <Row>
                        <FieldBlock label="Вид контроля (надзора)" labelFontSize="small">
                          <SelectInputField
                            id="supervisionType"
                            name="supervisionType"
                            options={erknmSupervisionTypesSelectOptions}
                            isSearchable
                          />
                        </FieldBlock>
                      </Row>
                    )}
                  </>
                )}
              </>
            )}
            {UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.CHECKLIST && (
              <Row>
                <FieldBlock label="Наименование" labelFontSize="small">
                  <TextInputField name="name" />
                </FieldBlock>
              </Row>
            )}
            {!isErknm && UploadFileTypesEnum[fileTypeId] !== UploadFileTypesEnum.CHECKLIST && (
              <Row>
                <FieldBlock label="Наименование Функции/Услуги ФРГУ" labelFontSize="small">
                  <SelectInputField
                    id="knoOrganizationFunction"
                    name="knoOrganizationFunction"
                    options={knoOrganisationFunctionsOptions}
                    isSearchable
                  />
                </FieldBlock>
              </Row>
            )}
            {UploadFileTypesEnum[fileTypeId] === UploadFileTypesEnum.CHECKLIST && (
              <Row>
                <FieldBlock label="Сведения об утверждении" labelFontSize="small">
                  <TextInputField name="approval" />
                </FieldBlock>
              </Row>
            )}
            <Row title="Файл" notSpace titleSize="small">
              <div className={styles.FileContent} id="fileAddButton">
                <Field name="file">
                  {props => {
                    function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
                      props.input.onChange(e.target.files?.[0]);
                      e.target.value = '';
                    }

                    return (
                      <input
                        id="file"
                        style={{ display: 'none' }}
                        type="file"
                        accept={validExtensionsString}
                        name={props.input.name}
                        onChange={handleFileInputChange}
                      />
                    );
                  }}
                </Field>
                <label className={styles.FileButton} htmlFor="file" id="selectFileButton">
                  Выберите файл
                </label>
                {(!file || !isFileValid) && <span className={styles.EmptyFileNotification}>Нет выбранных файлов</span>}
                {file && isFileValid && <UploadFile fileName={fileName} fileSize={fileSize} fileType={fileType} />}
                {file && !isFileValid && <span className={styles.FileError}>Неверный формат файла</span>}
              </div>
            </Row>
          </div>
        </ModalBody>
        <ModalActions>
          <Button
            color="primary"
            size="medium"
            text="Загрузить"
            id="uploadButton"
            type="submit"
            disabled={isDisabledSubmit}
          />
          <Button color="default" size="medium" text="Отменить" type="button" onClick={onClose} />
        </ModalActions>
      </form>
    );
  }

  return (
    <>
      <Modal modalBodyClassName={styles.Modal} visible={visible} onClose={onClose} afterClose={handleAfterClose}>
        <Form<IUploadModalForm> onSubmit={handleFormSubmit} render={renderForm} />
      </Modal>
    </>
  );
};

const mapStateToProps = (state: IRootState): IUploadModalStoreProps => {
  const knoOrganizationId = getImportKnoOrganizationId(state);
  const knoOrganizationErvkId = getImportKnoOrganizationErvkId(state);
  const supervisionTypeErvkId = getImportSupervisionTypeErvkId(state);
  const planTypeFederalLawErvkId = getImportPlanTypeFederalLawErvkId(state);
  const federalLawErvkId = getImportFederalLawErvkId(state);
  const knmTypeId = getImportKnmTypeId(state);
  const subjectTypeId = getImportSubjectTypeId(state);
  const getDictionariesSupervisionTypesSelectOptions = makeGetDictionariesSupervisionTypesExtendedSelectOptions();
  const getKnmSupervisionTypesExtendedSelectOptions = makeGetCatalogKnmSupervisionTypesExtendedSelectOptions();
  const getDictionariesTargetEntitySelectOptions = makeGetDictionariesTargetEntitySelectOptions();

  const federalLawErvkIds = (() => {
    if (planTypeFederalLawErvkId) {
      return [planTypeFederalLawErvkId];
    }
    if (federalLawErvkId) {
      return [federalLawErvkId];
    }

    return [];
  })();

  const nonFz248FederalLawIds = (getFilteredFederalLawsSelectOptions(state) ?? []).map(law => law.value.dictVersionId);

  return {
    isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
    plan: state.plan.data,
    planImportTemplate: getImportTemplate(state),
    subjectTypeErvkId: getImportSubjectTypeErvkId(state),
    userKnoOrganisationsSelectOptions: getAccountKnoOrganizationsSelectOptions(state),
    userProsecutorOrganisationsSelectOptions: getAccountProsecutorOrganizationsSelectOptions(state),
    knoOrganisationFunctionsOptions: knoOrganizationId
      ? getAccountKnoOrganizationFunctionsGroupSelectOptions(state, knoOrganizationId)
      : [],
    erknmKnoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
    isSupervisor: getUserRoleIsSupervisor(state),
    knoOrganizationId,
    knoOrganizationErvkId,
    supervisionTypeErvkId,
    knmTypeId,
    subjectTypeId,
    erknmSupervisionTypesSelectOptions: getDictionariesSupervisionTypesSelectOptions(
      state,
      knoOrganizationErvkId ? [knoOrganizationErvkId] : [],
      false,
      true,
    ),
    erknmSupervisionTypesExcluding248SelectOptions:
      nonFz248FederalLawIds.length > 0
        ? getDictionariesSupervisionTypesSelectOptions(
            state,
            knoOrganizationErvkId ? [knoOrganizationErvkId] : [],
            false,
            true,
            nonFz248FederalLawIds,
          )
        : [],
    erpSupervisionTypesSelectOptions:
      planTypeFederalLawErvkId || federalLawErvkId
        ? getDictionariesSupervisionTypesSelectOptions(
            state,
            knoOrganizationErvkId ? [knoOrganizationErvkId] : [],
            false,
            true,
            federalLawErvkIds,
          )
        : [],
    knmSupervisionTypesSelectOptions: getKnmSupervisionTypesExtendedSelectOptions(state, {
      knmTypeId,
      subjectTypeId,
    }),
    knmSubjectTypesErvkSelectOptions: getDictionariesTargetEntitySelectOptions(state, {
      knoVersionId: knoOrganizationErvkId || '',
      supervisionVersionId: supervisionTypeErvkId || '',
    }),
    federalLawsWithPlanTypeSelectOptions: getPlanTypeWithFederalLawSelectOptions(state),
    federalLawsSelectOptions: getFilteredFederalLawsSelectOptions(state),
  };
};

export const UploadModal = connect(mapStateToProps)(UploadModalWithoutStore);
